/** Combination artifacts: putting the set together, and taking it apart again.
 *
 * The content has shipped fifteen of these since the artifact table was
 * authored - Regalia of the Dawn, the Shroud of the Barrow King, the Power of the
 * Dragon Father and twelve more - and every one of them was unreachable. They
 * carry `rank: "combination"` and a `components` list, `load.ts` validates both
 * (a dangling component is a load error, so has a component list on something
 * that is not a combination), the merchant screen prints the rank, and the map
 * generator deliberately refuses to bury one:
 *
 *     .filter((a) => ranks.includes(a.rank) && a.components.length === 0)
 *
 * which is correct - a relic that is meant to be assembled should not be found
 * in a chest - and left them with no way into a game at all. This file is the
 * way in.
 *
 * Two rules and both are the classic's:
 *
 *  - **Assembly needs every component and the target slot.** The parts may be
 *    worn or in the bag, it makes no difference; what may not happen is a
 *    combination artifact displacing something that is not one of its own
 *    parts, because that would silently unequip an artifact the player chose.
 *  - **Disassembly gives the parts back.** Assembly frees slots (five artifacts
 *    become one) and is therefore never refused for want of room, but the
 *    reverse is a real decision: Regalia of the Dawn is worth more than its five
 *    parts in a fight and less than them when what you need is five slots.
 *    Without a way back it would be a trap rather than a choice.
 *
 * The three questions - can this be assembled, what can, can this be taken
 * apart - take a `ContentRegistry` rather than a `Ctx`, because they read
 * nothing but the artifact table and a hero. That is what lets the hero screen
 * ask them directly instead of mirroring the rule in the shell, which is how
 * the equip rules ended up stated twice (`heroModel.ts` still carries a
 * "mirrored test" of them). The two that WRITE take a `Ctx`, as everything that
 * writes does.
 *
 * Nothing here touches `GameState`'s shape. A combination artifact is an
 * artifact: it sits in `h.artifacts[slot]` like any other, `heroModifiers`
 * reads it out of the same loop, and a save written before this file loads
 * afterwards unchanged.
 */

import type { ArtifactDefId, HeroId } from "../ids.js";
import type { ArtifactDef, ContentRegistry } from "../content/registry.js";
import type { HeroState } from "../state.js";
import { slotIsFree, slotsFor, withSlots, wornSlotOf } from "./slots.js";
import type { Reason } from "../commands.js";
import type { Ctx } from "./ctx.js";
import { patchHero } from "./ctx.js";

/** Where one component of a set is right now. */
type Where =
  | { readonly at: "slot"; readonly slot: string }
  | { readonly at: "bag"; readonly index: number };

/**
 * Find every component of `def` on this hero, or null if any is missing.
 *
 * The bag is searched left to right and each hit is claimed, so a set that
 * needs two of the same artifact needs two of them in the bag - `components` is
 * a list rather than a set for exactly that reason, and no shipped set uses it
 * today. Being right about it costs one `claimed` array.
 */
export function locateComponents(
  h: HeroState, def: ArtifactDef,
): readonly Where[] | null {
  const out: Where[] = [];
  const claimed = new Set<number>();
  const wornClaimed = new Set<string>();

  for (const part of def.components) {
    let found: Where | null = null;
    for (const slot of Object.keys(h.artifacts).sort()) {
      if (wornClaimed.has(slot)) continue;
      if (h.artifacts[slot] !== part) continue;
      found = { at: "slot", slot };
      wornClaimed.add(slot);
      break;
    }
    if (found === null) {
      for (let i = 0; i < h.backpack.length; i++) {
        if (claimed.has(i) || h.backpack[i] !== part) continue;
        found = { at: "bag", index: i };
        claimed.add(i);
        break;
      }
    }
    if (found === null) return null;
    out.push(found);
  }
  return out;
}

/**
 * Why this hero may not put this set together, or null when they may.
 *
 * The same function the enumerator and the executor both call, so a command the
 * planner is offered is a command the kernel accepts (3.3).
 */
export function assembleIllegality(
  content: ContentRegistry, h: HeroState, art: ArtifactDefId,
): Reason | null {
  const def = content.artifacts.get(art);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: art as string };
  if (def.components.length === 0) return { code: "TARGET_INVALID" };
  // Already wearing it, or carrying a second one: the set is not assembled
  // twice onto the same hero.
  if (wornSlotOf(h, art) !== null || h.backpack.includes(art)) {
    return { code: "TARGET_INVALID" };
  }
  const found = locateComponents(h, def);
  if (found === null) return { code: "TARGET_INVALID" };
  if (assemblySlot(h, def, found) === null) return { code: "SLOT_OCCUPIED", slot: 0 };
  return null;
}

/**
 * The socket the finished artifact lands in: one of its family that is free,
 * or that holds a part about to be consumed - a Idol of the Host worn as five
 * trinkets takes the socket its first part vacates. Anything else sitting in
 * every socket belongs to the player, and assembling is not permission to take
 * it off; null then.
 */
function assemblySlot(
  h: HeroState, def: ArtifactDef, found: readonly Where[],
): string | null {
  const vacated = new Set<string>();
  for (const w of found) if (w.at === "slot") vacated.add(w.slot);
  for (const s of slotsFor(def)) if (slotIsFree(h, s) || vacated.has(s)) return s;
  return null;
}

/** Everything this hero could assemble right now, in a stable order. */
export function assemblableFor(
  content: ContentRegistry, h: HeroState,
): readonly ArtifactDefId[] {
  const out: ArtifactDefId[] = [];
  for (const def of content.artifacts.values()) {
    if (def.components.length === 0) continue;
    if (assembleIllegality(content, h, def.id) === null) out.push(def.id);
  }
  out.sort((a, b) => (a === b ? 0 : a < b ? -1 : 1));
  return out;
}

/** Put the set together. Assumes `assembleIllegality` already said yes. */
export function assemble(c: Ctx, heroId: HeroId, art: ArtifactDefId): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const def = c.content.artifacts.get(art);
  if (def === undefined) return;
  const found = locateComponents(h, def);
  if (found === null) return;
  const slot = assemblySlot(h, def, found);
  if (slot === null) return;

  // The parts leave their sockets, then the set takes its own.
  const changes: Record<string, ArtifactDefId | null> = {};
  const dropBag = new Set<number>();
  for (const w of found) {
    if (w.at === "slot") changes[w.slot] = null;
    else dropBag.add(w.index);
  }
  changes[slot] = art;
  const backpack = h.backpack.filter((_, i) => !dropBag.has(i));

  patchHero(c, heroId, { artifacts: withSlots(h, changes), backpack });
  c.events.push({ type: "ArtifactGained", hero: heroId, artifact: art });
  c.events.push({ type: "ArtifactEquipped", hero: heroId, artifact: art, slot });
}

/**
 * Why this hero may not take the artifact in `slot` apart.
 *
 * Only a worn one. A combination artifact in the bag has no slot to argue
 * about and nothing is gained by allowing it, and "the parts of what you are
 * wearing" is a rule a player can hold in their head.
 */
export function disassembleIllegality(
  content: ContentRegistry, h: HeroState, slot: string,
): Reason | null {
  const art = h.artifacts[slot];
  if (art === null || art === undefined) return { code: "SLOT_EMPTY", slot: 0 };
  const def = content.artifacts.get(art);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: art as string };
  if (def.components.length === 0) return { code: "TARGET_INVALID" };
  return null;
}

/** Take it apart: the slot empties and every part lands in the bag. */
export function disassemble(c: Ctx, heroId: HeroId, slot: string): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const art = h.artifacts[slot];
  if (art === null || art === undefined) return;
  const def = c.content.artifacts.get(art);
  if (def === undefined) return;

  // Into the bag rather than back onto the hero. Auto-equipping would have to
  // choose which parts to wear when the set is bigger than the slots it came
  // out of, and that choice is the player's.
  patchHero(c, heroId, {
    artifacts: withSlots(h, { [slot]: null }), backpack: [...h.backpack, ...def.components],
  });
  for (const part of def.components) {
    c.events.push({ type: "ArtifactGained", hero: heroId, artifact: part });
  }
}
